import { SENDABLE_SMS_LINE_TYPES } from '@provision/conversation-types';
import { useMutation } from '@provision/graphql-client';
import { useEffect, useMemo, useRef } from 'react';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import {
  PartnerGroupRules,
  PartnerSetting,
  usePartnerGroupRules,
  usePartnerSettings,
  UserGroupRules,
  useUserGroupRuleValues,
} from 'src/graphql';
import { findTwilioSmsParticipation } from 'src/pages/Conversations/Chat/InputField/utils';
import { useChatCommonData } from 'src/pages/Conversations/Chat/registry/common-data-context';
import {
  CHECK_SMS_DELIVERABILITY_MUTATION,
  type CheckSmsDeliverabilityMutationVars,
} from 'src/pages/Conversations/Chat/SmsSession/sms-session.operations';
import { isStringBooleanTrue } from 'src/utils/strings';
import { useOpenSmsSession } from './use-open-sms-session';

export interface SmsSwitchVisibility {
  smsGatesPass: boolean;
  showSmsSwitch: boolean;
  hasOpenSession: boolean;
  openSessionId: string | null;
  openSessionChannelId: string | null;
  sessionOpenedAt: string | null;
  /**
   * User-visible reason when the client's number is known non-sendable
   * (e.g. landline). Null while unchecked (optimistic) or when sendable.
   */
  smsDisabledReason: string | null;
}

/**
 * The switch shows whenever the gates pass — no TwilioSMS channel required, the
 * backend creates one at startSmsSession time. Deliverability only disables it:
 * a channel with a cached non-sendable line type renders the SMS tab inert.
 */
export function useSmsSwitchVisibility(conversationId: string): SmsSwitchVisibility {
  const conversationPartnerId = useChatCommonData((s) => s.partnerId);
  const conversationParticipations = useChatCommonData((s) => s.conversationParticipations);

  const partnerId = Number(conversationPartnerId);
  const { partnerGroupRules } = usePartnerGroupRules(partnerId);
  const { userGroupRules } = useUserGroupRuleValues();

  const { data: smsSettingRows } = usePartnerSettings({
    partnerId,
    partnerSetting: PartnerSetting.SmsSessionsOn,
  });

  const smsGatesPass = useMemo(() => {
    const settingRows = Array.isArray(smsSettingRows) ? smsSettingRows : [];
    const partnerLevel = settingRows.find((row) => row.partnerId !== PLATFORM_PARTNER_ID);
    const platformLevel = settingRows.find((row) => row.partnerId === PLATFORM_PARTNER_ID);
    const smsSettingOn = isStringBooleanTrue((partnerLevel ?? platformLevel)?.value ?? '');

    return (
      smsSettingOn &&
      partnerGroupRules[PartnerGroupRules.SmsSessionsAvailable] === true &&
      userGroupRules[UserGroupRules.AccessToSmsSessions] === true
    );
  }, [smsSettingRows, partnerGroupRules, userGroupRules]);

  const twilioSmsParticipation = useMemo(
    () => (smsGatesPass ? findTwilioSmsParticipation(conversationParticipations) : undefined),
    [smsGatesPass, conversationParticipations],
  );

  const showSmsSwitch = smsGatesPass;

  const {
    hasOpenSession,
    sessionId,
    channelId: sessionChannelId,
    openedAt,
  } = useOpenSmsSession(showSmsSwitch ? conversationId : '');

  const channel = twilioSmsParticipation?.channel ?? null;
  // The participation row carries the channel id; the nested channel comes from
  // ConversationChannelFields, which exposes it only as `id`.
  const channelId = twilioSmsParticipation?.conversationChannelId ?? null;
  // The entity layer stores the nullable GQL Strings as '' — normalize to null.
  const lineType = channel?.lineType || null;
  const lineTypeCheckedAt = channel?.lineTypeCheckedAt || null;

  const [checkDeliverabilityMutation] = useMutation<
    unknown,
    CheckSmsDeliverabilityMutationVars
  >(CHECK_SMS_DELIVERABILITY_MUTATION, {
    // A failed Lookup leaves lineTypeCheckedAt null, so the next visit retries. With onError
    // set, Apollo resolves the un-awaited call below instead of rejecting it.
    onError: (error) => console.warn('checkSmsDeliverability failed', error),
  });

  // Lazy Twilio Lookup: fire once per channel when it has never been checked.
  // The ref guards StrictMode double-effects; it resets on conversation change
  // so a silently-failed check gets retried on revisit.
  const checkedChannelIds = useRef<Set<string>>(new Set());
  const prevConversationIdRef = useRef(conversationId);
  if (prevConversationIdRef.current !== conversationId) {
    prevConversationIdRef.current = conversationId;
    checkedChannelIds.current.clear();
  }
  useEffect(() => {
    if (!channelId || lineTypeCheckedAt !== null) return;
    if (checkedChannelIds.current.has(channelId)) return;
    checkedChannelIds.current.add(channelId);
    void checkDeliverabilityMutation({
      variables: { input: { conversationChannelId: channelId } },
    });
  }, [channelId, lineTypeCheckedAt, checkDeliverabilityMutation]);

  // Unchecked numbers count as sendable while the lazy check runs in the background.
  const smsDisabledReason =
    lineTypeCheckedAt && lineType && !SENDABLE_SMS_LINE_TYPES.has(lineType)
      ? `This number can't receive texts (${lineType})`
      : null;

  return {
    smsGatesPass,
    showSmsSwitch,
    hasOpenSession,
    openSessionId: sessionId,
    openSessionChannelId: sessionChannelId,
    sessionOpenedAt: openedAt,
    smsDisabledReason,
  };
}
